import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "../components/ui/card";
import { Filter, Layers, Bot, TrendingUp, ArrowUp, ArrowDown } from "lucide-react";
import { apiRequest } from "../components/lib/queryClient";
import { ApiResponse } from "../components/lib/utils";

interface DashboardStats {
  activeFilters: number;
  tradingPairs: number;
  autoTradesToday: number;
  successRate: string;
}

interface Order {
  id: string;
  side: "buy" | "sell";
  pair: string;
  size: string;
  pnl?: string;
  createdAt: string;
}

export default function Dashboard() {

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["/api/dashboard/stats"],
    queryFn: async () => {
      const res = await apiRequest<ApiResponse<DashboardStats>>("GET", "/api/dashboard/stats");
      return res.data;
    },
  });

  const { data: recentOrders, isLoading: ordersLoading } = useQuery({
    queryKey: ["/api/order-history/recent", "limit=10"],
    queryFn: async () => {
      const res = await apiRequest<ApiResponse<Order[]>>("GET", "/api/order-history/recent?limit=10");
      return res.data;
    },
  });

  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="pt-6">
                <div className="h-20 bg-gray-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Filters</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.activeFilters || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                <Filter className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Trading Pairs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.tradingPairs || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-success bg-opacity-10 rounded-lg flex items-center justify-center">
                <Layers className="w-6 h-6 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Auto Trades Today</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.autoTradesToday || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-warning bg-opacity-10 rounded-lg flex items-center justify-center">
                <Bot className="w-6 h-6 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Success Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.successRate || "0%"}
                </p>
              </div>
              <div className="w-12 h-12 bg-success bg-opacity-10 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Trades</h3>
            {ordersLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse p-3 bg-gray-50 rounded-lg">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : recentOrders && recentOrders.length > 0 ? (
              <div className="space-y-4">
                {recentOrders.slice(0, 5).map((order: any) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${order.side === "buy"
                          ? "bg-success bg-opacity-10"
                          : "bg-error bg-opacity-10"
                        }`}>
                        {order.side === "buy" ? (
                          <ArrowUp className={`w-4 h-4 text-success`} />
                        ) : (
                          <ArrowDown className={`w-4 h-4 text-error`} />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{order.pair}</p>
                        <p className="text-sm text-gray-500">
                          {order.side.toUpperCase()} • Size: {order.size}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-medium ${order.pnl && parseFloat(order.pnl) > 0
                          ? "text-success"
                          : order.pnl && parseFloat(order.pnl) < 0
                            ? "text-error"
                            : "text-gray-500"
                        }`}>
                        {order.pnl ? `${parseFloat(order.pnl) > 0 ? "+" : ""}$${parseFloat(order.pnl).toFixed(2)}` : "Pending"}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(order.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent trades found</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                  <span className="text-gray-700">Trading System</span>
                </div>
                <span className="text-success font-medium">Active</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Platform Connections</span>
                <span className="font-medium">Connected</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Data Feed</span>
                <span className="font-medium text-success">Live</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Risk Management</span>
                <span className="font-medium text-success">Enabled</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
